import { useCallback, useRef, useSyncExternalStore } from 'react';
import { useParams } from 'react-router-dom';

const STORAGE_KEY_PREFIX = 'ditto:seen-updates';

function getStorageKey(workspaceUname: string) {
  return `${STORAGE_KEY_PREFIX}:${workspaceUname}`;
}

function readFromStorage(key: string): Set<string> {
  try {
    const raw = localStorage.getItem(key);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

const snapshotCache = new Map<string, Set<string>>();
const listeners = new Set<() => void>();

function getSnapshot(key: string): Set<string> {
  if (!snapshotCache.has(key)) {
    snapshotCache.set(key, readFromStorage(key));
  }
  return snapshotCache.get(key)!;
}

const subscribe = (callback: () => void) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

function notify(key: string) {
  snapshotCache.set(key, readFromStorage(key));
  listeners.forEach(cb => cb());
}

export const useSeenUpdates = () => {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const key = getStorageKey(workspaceUname!);
  const keyRef = useRef(key);
  keyRef.current = key;

  const seen = useSyncExternalStore(subscribe, () =>
    getSnapshot(keyRef.current)
  );

  const markSeen = useCallback((id: string) => {
    const current = readFromStorage(keyRef.current);
    if (current.has(id)) return;
    current.add(id);
    localStorage.setItem(keyRef.current, JSON.stringify([...current]));
    notify(keyRef.current);
  }, []);

  const hasSeen = useCallback((id: string) => seen.has(id), [seen]);

  return { hasSeen, markSeen };
};
